import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isJwtExpired } from '../../auth/jwt/tokenUtils';

interface User {
    id: string;
    email: string;
    name: string;
    role: string;
    avatar?: string;
}

interface AuthState {
    isAuthenticated: boolean;
    user: User | null;
    token: string | null;
}

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
};

// Check localStorage for existing auth data
const storedAuth = localStorage.getItem('adminAuth');
if (storedAuth) {
    try {
        const parsedAuth = JSON.parse(storedAuth);
        const token = parsedAuth.token;
        // Only restore if user is admin
        if (parsedAuth.user?.role === 'admin' && token && !isJwtExpired(token)) {
            initialState.isAuthenticated = true;
            initialState.user = parsedAuth.user;
            initialState.token = token;
        } else {
            // Clear non-admin auth
            localStorage.removeItem('adminAuth');
        }
    } catch (error) {
        console.error('Error parsing stored auth:', error);
        localStorage.removeItem('adminAuth');
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        login: (state, action: PayloadAction<{ user: User; token: string }>) => {
            // Only allow admin role
            if (action.payload.user.role !== 'admin') {
                throw new Error('Access denied. Admin privileges required.');
            }

            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.token = action.payload.token;

            // localStorage is already set by jwtService
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.token = null;

            // Clear localStorage
            localStorage.removeItem('adminAuth');
        },
        updateUser: (state, action: PayloadAction<Partial<User>>) => {
            if (state.user) {
                state.user = { ...state.user, ...action.payload };

                // Update localStorage
                localStorage.setItem('adminAuth', JSON.stringify({
                    user: state.user,
                    token: state.token,
                }));
            }
        },
    },
});

export const { login, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
